<template>
  <div class="deviation-survey-depth-chart" :class="{ isStatic }">
    <ScatterChart
      v-bind="$attrs"
      draw-x-axis
      draw-y-axis
      :data-sets="allDatasets"
      :tick-frequency-x="ddvTickFrequencies?.x"
      :tick-frequency-y="ddvTickFrequencies?.y"
    >
      <template
        v-if="dataSet.points.length >= 3"
        #perSeries="{
          screenPoints,
          nearestPoint,
          xScaler,
          yScaler,
          dataSeriesNumber
        }"
      >
        <LinePlot
          v-if="dataSeriesNumber === 0"
          is-active
          :data-series-number="dataSeriesNumber"
          :screen-points="screenPoints"
          curve-interpolator="catmull-rom"
        />

        <g
          v-else
          class="operating-point"
          :data-series-number="dataSeriesNumber"
          :transform="`
            translate(
              ${xScaler.scale(allDatasets[dataSeriesNumber].points[0].x)},
              ${yScaler.scale(allDatasets[dataSeriesNumber].points[0].y)}
            )`"
        >
          <g>
            <circle
              :cx="0"
              :cy="0"
              :r="5"
              stroke="var(--activePlotColor)"
              :stroke-width="2"
              fill="transparent"
            />
            <circle
              :cx="0"
              :cy="0"
              :r="5"
              stroke="var(--activePlotColor)"
              :stroke-opacity="0.3"
              :stroke-width="8"
              fill="transparent"
            />
            <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
          </g>
        </g>

        <NearestPoint
          v-if="dataSeriesNumber > 0"
          :data-series-number="dataSeriesNumber"
          :nearest-point="nearestPoint"
        />
      </template>

      <template
        v-if="dataSet.points.length < 3"
        #background="{ domState, margins }"
      >
        <rect
          v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState.plotWidth"
          :height="domState.plotHeight"
        />
        <text
          x="50%"
          y="50%"
          fill="var(--plotColor0)"
          text-anchor="middle"
          dominant-baseline="middle"
          >No Data</text
        >
      </template>

      <template v-if="dataSet.points.length < 3" #crosshairs>
        Return none
      </template>
    </ScatterChart>
  </div>
</template>

<script setup lang="ts">
import type { DataSet, NumericPoint } from "@incytestudios/ddv/types/common";
import type { Scenario } from "@/composables/forms/formTypes";

import { computed, inject, ref } from "vue";
import { injectStrict } from "@/utils/utils";
import { AngleUnits } from "@/libs/units/angle";
import { ScatterChart, LinePlot, NearestPoint } from "@incytestudios/ddv";
import type {
  DeviationSurveyEntryRows,
  ScenarioCalculations
} from "@/composables/scenarioCalculations/calculationTypes";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useEquipmentIndexes } from "@/composables/equipment/deviationSurvey/useEquipmentIndexes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";

const props = defineProps<{
  isStatic?: boolean;
  scenario?: Scenario | null;
  surveyType?: DeviationSurveyEntryRows["surveyType"] | null;
  entryRows?: DeviationSurveyEntryRows["data"];
  scenarioCalculations?: ScenarioCalculations;
}>();

const scenario = props.scenario
  ? ref<Scenario>(props.scenario)
  : injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const scenarioCalculations =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const surveyType = computed(
  () => props.surveyType ?? scenarioCalculations.entryRows.value.surveyType
);
const entryRows = computed(
  () => props.entryRows ?? scenarioCalculations.entryRows.value.data
);

const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

// The override keys used here will hopefully go away if we add unit controls
// specifically for the pump table/charts
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const xAxisLabel = computed(() => {
  return `Deviation (${
    targetLargeLengthUnit.value?.shortLabel || targetLargeLengthUnit.value.label
  })`;
});
const yAxisLabel = computed(() => {
  return `Vertical Depth (${
    targetLargeLengthUnit.value?.shortLabel || targetLargeLengthUnit.value.label
  })`;
});
const dataSet = computed((): DataSet => {
  let points: NumericPoint[] = [];
  let prevMD = 0;
  let curMD = 0;
  let inclinationRadians = 0;
  let lastX = 0;

  points = entryRows.value.map((curRow: number[], idx: number) => {
    if (idx === 0 || entryRows.value.length < 2) {
      return { x: 0, y: 0 };
    }
    prevMD = entryRows.value[idx - 1][0];
    curMD = curRow[0];
    inclinationRadians = AngleUnits.Radians.toThisUnit(
      AngleUnits.Degrees.toStandardUnit(90 - curRow[1])
    );
    lastX =
      lastX + (curMD - prevMD) * Math.sin(Math.PI / 2 - inclinationRadians);

    return {
      x: targetLargeLengthUnit.value.toThisUnit(lastX),
      y: targetLargeLengthUnit.value.toThisUnit(curRow[3])
    };
  });
  return {
    title: "Vertical Depth",
    xAxisLabel: xAxisLabel.value,
    yAxisLabel: yAxisLabel.value,
    reverseYAxis: true,
    points
  };
});

const { chartedEquipmentIndexDatasets } = useEquipmentIndexes(
  entryRows.value,
  surveyType.value,
  scenarioCalculations,
  dataSet.value.points,
  xAxisLabel.value,
  yAxisLabel.value
);

const allDatasets = computed((): DataSet[] => {
  return [dataSet.value, ...chartedEquipmentIndexDatasets.value] as DataSet[];
});
</script>

<style lang="scss" scoped>
.disabled {
  :deep(.ddv) {
    &.isPlotHovered {
      cursor: auto !important;
    }
  }
}

.deviation-survey-depth-chart {
  width: 95%;
  padding: 5px;
}
</style>
